import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchCompletionQuery } from '../../../api/search';
import useInputState from '../../../hooks/useInputState';

export default () => {
  const timeout = useRef<NodeJS.Timeout>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, onQueryChange, setQuery] = useInputState('');
  const [completionQuery, setCompletionQuery] = useState('');
  const [focusQuery, setFocusQuery] = useState<string | undefined>();
  const { data } = useSearchCompletionQuery(completionQuery);
  const [index, setIndex] = useState(-1);

  const uniques = useMemo(
    () =>
      data
        ?.map((s) => s.text.trimEnd())
        ?.filter(
          (s, i, self) => self.findIndex((ss) => ss.toLowerCase() === s.toLocaleLowerCase()) === i,
        ),
    [data],
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const length = uniques?.length || 0;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const nextIndex = index === -1 ? length - 1 : index - 1;
        setIndex(nextIndex);
        setFocusQuery(uniques?.[nextIndex]);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = index + 1 >= length ? -1 : index + 1;
        setIndex(nextIndex);
        setFocusQuery(uniques?.[nextIndex]);
        return;
      }
    };

    inputRef.current?.removeEventListener('keydown', onKeyDown);

    inputRef.current?.addEventListener('keydown', onKeyDown);

    return () => inputRef.current?.removeEventListener('keydown', onKeyDown);
  }, [uniques, index]);

  useEffect(() => setFocusQuery(undefined), [query]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => setCompletionQuery(query), 500);

    return () => timeout.current && clearTimeout(timeout.current);
  }, [query]);

  useEffect(() => setIndex(-1), [query]);

  const reset = useCallback(() => {
    setQuery('');
    setCompletionQuery('');
    setFocusQuery(undefined);
  }, []);

  const suggestions = useMemo(
    () =>
      uniques?.map((s, i) => ({
        onHover: () => setIndex(i),
        selected: i === index,
        title: s,
        url: `/sok?q=${s}`,
      })),
    [uniques, index],
  );

  return {
    inputRef,
    onQueryChange,
    query: focusQuery || query,
    reset,
    suggestions,
  };
};
